<template>
  <div class="flex flex-col min-h-screen">
    <main class="flex-grow">
      <div v-if="pageState === 'MIX'">
        <div
          class="md:bg-transparent bg-white py-2 px-6 flex flex-col items-center"
        >
          <div class="flex items-center w-full">
            <div class="w-1/3 items-center flex">
              <button
                v-if="previousQuestionId"
                @click="goToPreviousQuestion"
                class="flex items-center space-x-1 border border-gray-300 rounded-xl px-2 hover:border-secondary"
              >
                <img src="../assets/back.svg" alt="Back" class="h-8" />
                <span class="hidden md:inline">{{ $t('back') }}</span>
              </button>
            </div>

            <div class="w-1/3 items-center justify-center flex">
              <LogoSmall class="md:hidden" />
              <LogoBig class="hidden md:flex" className="h-14 mb-1" />
            </div>

            <div class="w-1/3 items-center flex justify-end">
              <button
                @click="showExitConfirmation = true"
                class="flex items-center h-8 border border-gray-300 rounded-xl p-4 hover:border-secondary"
              >
                {{ $t('exit') }}
              </button>
            </div>
          </div>
          <div class="flex justify-center py-3">
            <div class="grid grid-flow-col gap-1 auto-cols-fr">
              <div
                v-for="(area, index) in areas"
                :key="index"
                class="flex flex-col items-center justify-center w-full"
              >
                <div class="relative w-12 h-12">
                  <svg
                    class="w-full h-full"
                    viewBox="0 0 36 36"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      class="circle-bg"
                      cx="18"
                      cy="18"
                      r="13.915"
                      stroke-width="2"
                      fill="none"
                    />
                    <circle
                      class="circle-progress fill-tertiary"
                      cx="18"
                      cy="18"
                      r="13.915"
                      stroke-width="2"
                      :stroke-dasharray="circumference"
                      :stroke-dashoffset="
                        circumference -
                        (circumference * areaProgress[index]) / 100
                      "
                      transform="rotate(-90 18 18)"
                    />
                  </svg>
                  <div
                    class="absolute inset-0 flex items-center justify-center"
                  >
                    <span class="font-bold">
                      <template v-if="areaProgress[index] === 100">✔</template>
                      <template v-else>{{ index + 1 }}</template>
                    </span>
                  </div>
                </div>
                <div
                  class="text-sm mt-1 text-center w-full truncate px-2"
                  :class="{ 'font-bold': areaProgress[index] === 100 }"
                >
                  {{ area.name }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="areas && areas.length > 0" class="p-4 space-y-8">
          <div class="space-y-4">
            <transition name="fade" mode="out-in">
              <div
                v-if="currentQuestion"
                :key="currentQuestion.questionId"
                class="space-y-4 flex flex-col justify-center items-center text-center"
              >
                <p
                  v-html="currentQuestion.questionText"
                  class="text-lg max-w-[900px]"
                ></p>

                <div
                  v-if="version === 'v2'"
                  class="max-w-[400px] space-y-4 w-full"
                >
                  <div
                    v-if="
                      currentQuestion.answers &&
                      currentQuestion.answers.length === 1 &&
                      currentQuestion.answers[0].type === 'date'
                    "
                  >
                    <input
                      type="date"
                      :name="`question-${currentQuestion.questionId}`"
                      v-model="answers[currentQuestion.questionId]"
                      @change="handleStandaloneInputChange()"
                      class="block w-full px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg focus:border-secondary focus:ring-secondary"
                    />
                  </div>

                  <div
                    v-else-if="
                      currentQuestion.answers &&
                      currentQuestion.answers.length === 1 &&
                      currentQuestion.answers[0].type === 'text'
                    "
                  >
                    <textarea
                      :name="`question-${currentQuestion.questionId}`"
                      v-model="answers[currentQuestion.questionId]"
                      @change="handleStandaloneInputChange()"
                      @input="validateField(currentQuestion)"
                      @blur="validateField(currentQuestion)"
                      @keyup.enter.exact="tryAdvanceInput(currentQuestion)"
                      class="block w-full px-4 py-2 text-left border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg focus:border-secondary focus:ring-secondary"
                      :class="{
                        'border-red-500':
                          fieldErrors[currentQuestion.questionId],
                      }"
                      :rows="currentQuestion.answers[0].rows || 3"
                      :maxlength="currentQuestion.answers[0].maxLength"
                      required
                    ></textarea>
                    <span
                      v-if="fieldErrors[currentQuestion.questionId]"
                      class="text-red-500 text-sm mt-1 block"
                    >
                      {{ fieldErrors[currentQuestion.questionId] }}
                    </span>
                  </div>

                  <div
                    v-else-if="
                      currentQuestion.answers &&
                      currentQuestion.answers.length === 1 &&
                      currentQuestion.answers[0].type === 'number'
                    "
                  >
                    <input
                      type="number"
                      :name="`question-${currentQuestion.questionId}`"
                      v-model.number="answers[currentQuestion.questionId]"
                      @change="handleStandaloneInputChange()"
                      @input="validateField(currentQuestion)"
                      @blur="validateField(currentQuestion)"
                      @keyup.enter="tryAdvanceInput(currentQuestion)"
                      class="block w-full px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg focus:border-secondary focus:ring-secondary"
                      :class="{
                        'border-red-500':
                          fieldErrors[currentQuestion.questionId],
                      }"
                      :step="currentQuestion.answers[0].step || '1'"
                      :min="currentQuestion.answers[0].min"
                      :max="currentQuestion.answers[0].max"
                      required
                    />
                    <span
                      v-if="fieldErrors[currentQuestion.questionId]"
                      class="text-red-500 text-sm mt-1 block"
                    >
                      {{ fieldErrors[currentQuestion.questionId] }}
                    </span>
                  </div>

                  <div
                    v-else-if="
                      currentQuestion.answers &&
                      currentQuestion.answers.length === 1 &&
                      currentQuestion.answers[0].type === 'dropdown'
                    "
                  >
                    <select
                      :name="`question-${currentQuestion.questionId}`"
                      v-model="answers[currentQuestion.questionId]"
                      @change="handleSelectChange()"
                      class="block w-full px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg focus:border-secondary focus:ring-secondary appearance-none"
                    >
                      <option disabled :value="undefined">
                        {{ $t('select-option') }}
                      </option>
                      <option
                        v-for="option in currentQuestion.answers[0].options"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.text }}
                      </option>
                    </select>
                  </div>

                  <div v-else-if="typeof currentQuestion.answers === 'string'">
                    <div
                      v-if="predefinedAnswerSets[currentQuestion.answers]"
                      class="space-y-4"
                    >
                      <div
                        v-for="(answer, aIndex) in predefinedAnswerSets[
                          currentQuestion.answers
                        ]"
                        :key="aIndex"
                        class="w-full flex items-center"
                      >
                        <label class="w-full cursor-pointer">
                          <input
                            type="radio"
                            :name="`question-${currentQuestion.questionId}`"
                            :value="answer.value"
                            v-model="answers[currentQuestion.questionId]"
                            @click="handleAnswerSelect(currentQuestion, answer)"
                            class="hidden peer"
                          />
                          <span
                            class="block px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg peer-hover:border-secondary peer-checked:bg-primary peer-checked:border-secondary peer-checked:font-bold transition duration-200 ease-in-out"
                            tabindex="0"
                            @keydown.space.prevent="
                              handleAnswerSelect(currentQuestion, answer)
                            "
                            @keydown.enter.prevent="
                              handleAnswerSelect(currentQuestion, answer)
                            "
                          >
                            {{ answer.text }}
                          </span>
                        </label>
                      </div>
                    </div>
                    <div v-else class="text-red-500">
                      Error: Unknown answer set '{{ currentQuestion.answers }}'
                    </div>
                  </div>

                  <div
                    v-else-if="Array.isArray(currentQuestion.answers)"
                    class="space-y-4"
                  >
                    <div
                      v-for="(answer, aIndex) in currentQuestion.answers"
                      :key="aIndex"
                      class="w-full"
                    >
                      <div class="flex items-center">
                        <label class="w-full cursor-pointer">
                          <input
                            type="radio"
                            :name="`question-${currentQuestion.questionId}`"
                            :value="answer.value"
                            v-model="answers[currentQuestion.questionId]"
                            @click="handleAnswerSelect(currentQuestion, answer)"
                            class="hidden peer"
                          />
                          <span
                            class="block w-full min-h-[48px] px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg peer-hover:border-secondary peer-checked:bg-primary peer-checked:border-secondary peer-checked:font-bold transition duration-200 ease-in-out flex items-center justify-center"
                            :class="{
                              'border-red-500':
                                fieldErrors[currentQuestion.questionId],
                            }"
                            tabindex="0"
                            @keydown.space.prevent="
                              handleAnswerSelect(currentQuestion, answer)
                            "
                            @keydown.enter.prevent="
                              handleAnswerSelect(currentQuestion, answer)
                            "
                          >
                            {{ answer.text || '—' }}
                          </span>
                        </label>
                      </div>
                      <div
                        v-if="
                          (answer.type === 'text' ||
                            answer.type === 'number') &&
                          answers[currentQuestion.questionId] === answer.value
                        "
                        class="mt-2"
                      >
                        <input
                          :type="answer.type === 'number' ? 'number' : 'text'"
                          :name="`question-${currentQuestion.questionId}-input`"
                          v-model="
                            answers[currentQuestion.questionId + '_input']
                          "
                          @keyup.enter="tryAdvanceInput(currentQuestion)"
                          @input="validateField(currentQuestion)"
                          @blur="storeInputOnBlur()"
                          class="block w-full px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg focus:border-secondary focus:ring-secondary"
                          :class="{
                            'border-red-500':
                              fieldErrors[currentQuestion.questionId],
                          }"
                          required
                        />
                        <span
                          v-if="fieldErrors[currentQuestion.questionId]"
                          class="text-red-500 text-sm mt-1 block"
                        >
                          {{ fieldErrors[currentQuestion.questionId] }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="currentFollowUpQuestions.length > 0"
                    class="mt-6 pt-6 border-t border-gray-300 space-y-6"
                  >
                    <div
                      v-for="followUpQ in currentFollowUpQuestions"
                      :key="followUpQ.questionId"
                      class="space-y-3 p-4 bg-gray-50 rounded-lg"
                    >
                      <p
                        v-html="followUpQ.questionText"
                        class="text-md font-semibold"
                      ></p>
                      <div class="max-w-[400px] space-y-4 w-full mx-auto">
                        <div
                          v-if="
                            followUpQ.answers &&
                            followUpQ.answers.length === 1 &&
                            followUpQ.answers[0].type === 'text'
                          "
                        >
                          <input
                            type="text"
                            :name="`question-${followUpQ.questionId}`"
                            v-model="answers[followUpQ.questionId]"
                            @change="handleFollowUpChange()"
                            @input="validateField(followUpQ)"
                            @blur="validateField(followUpQ)"
                            class="block w-full px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg focus:border-secondary focus:ring-secondary"
                            :class="{
                              'border-red-500':
                                fieldErrors[followUpQ.questionId],
                            }"
                            required
                          />
                          <span
                            v-if="fieldErrors[followUpQ.questionId]"
                            class="text-red-500 text-sm mt-1 block"
                          >
                            {{ fieldErrors[followUpQ.questionId] }}
                          </span>
                        </div>

                        <div
                          v-else-if="
                            followUpQ.answers &&
                            followUpQ.answers.length === 1 &&
                            followUpQ.answers[0].type === 'date'
                          "
                        >
                          <input
                            type="date"
                            :name="`question-${followUpQ.questionId}`"
                            v-model="answers[followUpQ.questionId]"
                            @change="handleFollowUpChange()"
                            class="input-style"
                          />
                        </div>

                        <div
                          v-else-if="
                            followUpQ.answers &&
                            followUpQ.answers.length === 1 &&
                            followUpQ.answers[0].type === 'number'
                          "
                        >
                          <input
                            type="number"
                            :name="`question-${followUpQ.questionId}`"
                            v-model="answers[followUpQ.questionId]"
                            @change="handleFollowUpChange()"
                            class="input-style"
                          />
                        </div>

                        <div
                          v-else-if="
                            followUpQ.answers &&
                            followUpQ.answers.length === 1 &&
                            followUpQ.answers[0].type === 'dropdown'
                          "
                        >
                          <select
                            :name="`question-${followUpQ.questionId}`"
                            v-model="answers[followUpQ.questionId]"
                            @change="handleFollowUpChange(followUpQ)"
                            class="block w-full px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg focus:border-secondary focus:ring-secondary appearance-none"
                            required
                          >
                            <option disabled :value="undefined">
                              {{ $t('select-option') }}
                            </option>
                            <option
                              v-for="option in followUpQ.answers[0].options"
                              :key="option.value"
                              :value="option.value"
                            >
                              {{ option.text }}
                            </option>
                          </select>
                          <span
                            v-if="fieldErrors[followUpQ.questionId]"
                            class="text-red-500 text-sm mt-1 block"
                          >
                            {{ fieldErrors[followUpQ.questionId] }}
                          </span>
                        </div>

                        <div
                          v-else-if="Array.isArray(followUpQ.answers)"
                          class="space-y-4"
                        >
                          <div
                            v-for="(followUpA, fuAIndex) in followUpQ.answers"
                            :key="fuAIndex"
                            class="w-full"
                          >
                            <div class="flex items-center">
                              <label class="w-full cursor-pointer">
                                <input
                                  type="radio"
                                  :name="`question-${followUpQ.questionId}`"
                                  :value="followUpA.value"
                                  v-model="answers[followUpQ.questionId]"
                                  @click="
                                    handleFollowUpAnswerSelect(
                                      followUpQ,
                                      followUpA
                                    )
                                  "
                                  class="hidden peer"
                                />
                                <span
                                  class="block w-full min-h-[48px] px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg peer-hover:border-secondary peer-checked:bg-primary peer-checked:border-secondary peer-checked:font-bold transition duration-200 ease-in-out flex items-center justify-center"
                                  :class="{
                                    'border-red-500':
                                      fieldErrors[followUpQ.questionId],
                                  }"
                                  tabindex="0"
                                  @keydown.space.prevent="
                                    handleAnswerSelect(followUpQ, followUpA)
                                  "
                                  @keydown.enter.prevent="
                                    handleAnswerSelect(followUpQ, followUpA)
                                  "
                                >
                                  {{ followUpA.text || '—' }}
                                </span>
                              </label>
                            </div>
                            <div
                              v-if="
                                (followUpA.type === 'text' ||
                                  followUpA.type === 'number') &&
                                answers[followUpQ.questionId] ===
                                  followUpA.value
                              "
                              class="mt-2"
                            >
                              <input
                                :type="
                                  followUpA.type === 'number'
                                    ? 'number'
                                    : 'text'
                                "
                                :name="`question-${followUpQ.questionId}-input`"
                                v-model="
                                  answers[followUpQ.questionId + '_input']
                                "
                                @input="validateField(followUpQ)"
                                @blur="validateField(followUpQ)"
                                class="block w-full px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg focus:border-secondary focus:ring-secondary"
                                :class="{
                                  'border-red-500':
                                    fieldErrors[followUpQ.questionId],
                                }"
                                required
                              />
                              <span
                                v-if="fieldErrors[followUpQ.questionId]"
                                class="text-red-500 text-sm mt-1 block"
                              >
                                {{ fieldErrors[followUpQ.questionId] }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="max-w-[400px] space-y-4">
                  <div
                    v-for="(answer, aIndex) in currentQuestion.answers"
                    :key="aIndex"
                    class="w-full flex items-center"
                  >
                    <label class="w-full cursor-pointer">
                      <input
                        type="radio"
                        :name="`question-${currentQuestion.questionId}`"
                        :value="answer.value"
                        v-model="answers[currentQuestion.questionId]"
                        @click="handleAnswerSelect(currentQuestion, answer)"
                        class="hidden peer"
                      />
                      <span
                        class="block px-4 py-2 text-center border border-gray-300 text-black bg-white rounded-xl shadow-grey shadow-lg peer-hover:border-secondary peer-checked:bg-primary peer-checked:border-secondary peer-checked:font-bold transition duration-200 ease-in-out"
                        tabindex="0"
                        @keydown.space.prevent="
                          handleAnswerSelect(currentQuestion, answer)
                        "
                        @keydown.enter.prevent="
                          handleAnswerSelect(currentQuestion, answer)
                        "
                      >
                        {{ answer.text }}
                      </span>
                    </label>
                  </div>
                </div>
                <div
                  v-if="
                    nextQuestionId === null &&
                    canProceed &&
                    !hasUnansweredFollowUps()
                  "
                  class="mt-4"
                >
                  <button
                    @click="submitAnswers"
                    class="px-4 py-2 bg-secondary text-white rounded-md hover:bg-opacity-80"
                    :disabled="pageState === 'SENDING'"
                  >
                    {{ $t('submit') }}
                  </button>
                </div>
                <div
                  v-if="
                    version === 'v2' &&
                    shouldShowNextButton &&
                    !isProcessingAnswer
                  "
                  class="mt-4"
                >
                  <transition name="fade">
                    <button
                      v-if="shouldShowNextButton"
                      @click="goToNextQuestion"
                      class="px-6 py-3 bg-primary text-black rounded-xl shadow-grey shadow-lg hover:bg-opacity-90 hover:shadow-md transform transition-all duration-200 ease-in-out font-medium"
                    >
                      {{ $t('next') }}
                    </button>
                  </transition>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <div v-else class="p-4 text-center">
          <p class="text-gray-500">{{ errorMessage || $t('loading') }}</p>
        </div>
        <DisclaimerModal
          v-if="showDisclaimerModal"
          :message="disclaimerModalText"
          @close="closeDisclaimerModal"
        />
        <div
          v-if="showIncompleteModal"
          class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
        >
          <div class="bg-white p-6 rounded-xl shadow-lg max-w-md w-full">
            <p class="text-lg mb-4">
              {{ $t('loaded-previous-attempt', { date: incompleteDate }) }}
            </p>
            <div class="mt-4 flex justify-center">
              <button
                @click="showIncompleteModal = false"
                class="px-4 py-2 text-white bg-secondary hover:bg-opacity-80 rounded-md"
              >
                {{ $t('ok') }}
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="showExitConfirmation"
          class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
        >
          <div class="bg-white p-6 rounded-xl shadow-lg max-w-md w-full">
            <p class="text-lg mb-4">{{ $t('exit-confirmation') }}</p>
            <div class="mt-4 flex justify-center space-x-4">
              <button
                @click="showExitConfirmation = false"
                class="px-4 py-2 text-black bg-gray-200 hover:bg-gray-300 rounded-md"
              >
                {{ $t('exit-cancel') }}
              </button>
              <button
                @click="confirmExit"
                class="px-4 py-2 text-white bg-secondary hover:bg-opacity-80 rounded-md"
              >
                {{ $t('exit-confirm') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="pageState === 'SENDING'" class="mt-10 px-4">
        <PsychologyFactLoader
          :title="$t('sending-mix')"
          :subtitle="$t('sending-mix-subtitle')"
        />
      </div>
      <div v-if="pageState === 'SENT'" class="mt-10 px-4">
        <div
          class="max-w-md mx-auto bg-white rounded-3xl shadow-lg px-6 pt-10 pb-8 flex flex-col items-center text-center relative overflow-hidden"
        >
          <div class="absolute inset-0 pointer-events-none sending-background"></div>
          <div class="relative z-10 w-full">
            <div class="text-lg font-semibold text-gray-900 mb-3">
              {{
                type === 'matchmind' ? $t('sent-mix-matchmind') : $t('sent-mix')
              }}
            </div>
            <div class="text-sm text-gray-600 leading-relaxed space-y-2">
              <p>{{ $t('sent-mix-subtitle') }}</p>
            </div>
          </div>
        </div>
      </div>
      <div v-if="pageState === 'ERROR'" class="mt-10 px-4">
        <div
          class="max-w-md mx-auto bg-white rounded-3xl shadow-lg px-6 pt-10 pb-8 flex flex-col items-center text-center relative overflow-hidden"
        >
          <div class="absolute inset-0 pointer-events-none sending-background"></div>
          <div class="relative z-10 w-full">
            <div class="text-lg font-semibold text-gray-900 mb-3">
              {{ $t('error-mix') }}
            </div>
            <div class="text-sm text-gray-600 leading-relaxed space-y-2">
              <p>{{ $t('error-mix-subtitle') }}</p>
            </div>
          </div>
        </div>
      </div>
    </main>
    <MedeaFooter />
  </div>
</template>

<script>
import {
  fetchQuestions,
  fetchScores,
  saveQuestionnaire,
} from '@/services/medea-api';
import DisclaimerModal from './DisclaimerModal.vue';
import MedeaFooter from './MedeaFooter.vue';
import LogoSmall from './LogoSmall.vue';
import LogoBig from './LogoBig.vue';
import PsychologyFactLoader from './PsychologyFactLoader.vue';
import { useI18n } from 'vue-i18n';
import { useGlobalStore } from '@/stores/global';
import { clients } from '@/config/clients';
import { trackAppTiming } from '@/utils/metrics';

export default {
  components: {
    DisclaimerModal,
    MedeaFooter,
    LogoSmall,
    LogoBig,
    PsychologyFactLoader,
  },
  data() {
    return {
      areas: null,
      answers: {},
      currentFollowUpQuestions: [],
      predefinedAnswerSets: {},
      version: null,
      currentQuestionId: null,
      previousQuestionId: null,
      nextQuestionId: null,
      errorMessage: null,
      showDisclaimerModal: false,
      disclaimerModalText: '',
      areaProgress: [],
      circumference: 2 * Math.PI * 13.915,
      pageState: 'MIX',
      isProcessingAnswer: false,
      showExitConfirmation: false,
      fieldErrors: {},
      showIncompleteModal: false,
      incompleteDate: null,
      questionnaireId: null,
      questionCounter: 0,
    };
  },

  computed: {
    type() {
      return this.$route.query.type;
    },
    currentQuestion() {
      // Find the current question based on currentQuestionId
      const allQuestions = this.areas.flatMap((area) => area.questions);
      return allQuestions.find(
        (question) => question.questionId === this.currentQuestionId
      );
    },
    shouldShowNextButton() {
      // Only for v2
      if (
        this.version !== 'v2' ||
        !this.currentQuestion ||
        this.autoAdvancing ||
        this.nextQuestionId === null
      )
        return false;

      const q = this.currentQuestion;

      // First check if this is a multiple choice question without follow-ups
      const isMultipleChoice =
        Array.isArray(q.answers) &&
        q.answers.length > 1 &&
        !q.answers.some((a) =>
          ['dropdown', 'date', 'text', 'number'].includes(a.type)
        );

      const hasNoFollowUps =
        !q.followUpQuestions || Object.keys(q.followUpQuestions).length === 0;

      // Never show Next for multiple choice without follow-ups
      if (isMultipleChoice && hasNoFollowUps) {
        return false;
      }

      // 1. If there are follow-ups, and all are filled out, show Next
      if (
        this.currentFollowUpQuestions.length > 0 &&
        !this.hasUnansweredFollowUps()
      ) {
        return true;
      }

      // 2. If the current question is a unique input type (dropdown, date, text, number)
      // and is filled out, show Next
      if (
        q.answers &&
        q.answers.length === 1 &&
        ['dropdown', 'date', 'text', 'number'].includes(q.answers[0].type)
      ) {
        // Check if filled out
        const val = this.answers[q.questionId];
        if (
          val !== undefined &&
          val !== null &&
          (typeof val !== 'string' || val.trim() !== '')
        ) {
          // For text/number, also check for required input
          if (
            ['text', 'number'].includes(q.answers[0].type) &&
            (val === '' || val === undefined || val === null)
          ) {
            return false;
          }
          return true;
        }
      }

      // 3. If the current question needs follow-ups but none are shown yet
      // (This means we're on a previously answered question that could have follow-ups)
      if (
        q.followUpQuestions &&
        Object.keys(q.followUpQuestions).length > 0 &&
        this.answers[q.questionId] !== undefined &&
        this.currentFollowUpQuestions.length === 0
      ) {
        // Check if there should be follow-ups for the current answer
        const currentAnswer = this.answers[q.questionId];
        if (q.followUpQuestions[currentAnswer]) {
          return true;
        }
      }

      // 4. If going back to a regular question with an answer but no follow-ups are needed
      // Don't show the Next button if it's a simple selection without follow-ups
      if (
        this.answers[q.questionId] !== undefined &&
        hasNoFollowUps &&
        this.nextQuestionId !== null
      ) {
        // Show Next if:
        // 1. It's an input type question (dropdown, date, text, number)
        // 2. OR if the selected answer requires text input AND that input is filled
        const isInputType =
          q.answers?.length === 1 &&
          ['dropdown', 'date', 'text', 'number'].includes(q.answers[0].type);
        const selectedAnswer = Array.isArray(q.answers)
          ? q.answers.find((a) => a.value === this.answers[q.questionId])
          : null;
        const selectedAnswerNeedsInput =
          selectedAnswer &&
          (selectedAnswer.type === 'text' || selectedAnswer.type === 'number');
        const inputIsFilled = selectedAnswerNeedsInput
          ? this.answers[q.questionId + '_input'] !== undefined &&
            this.answers[q.questionId + '_input'] !== null &&
            this.answers[q.questionId + '_input'] !== ''
          : true;
        return (isInputType || selectedAnswerNeedsInput) && inputIsFilled;
      }

      return false;
    },
    canProceed() {
      // Computed property version - doesn't modify reactive state to prevent infinite loops
      if (this.version !== 'v2') {
        return this.answers[this.currentQuestion?.questionId] !== undefined;
      }

      const currentQ = this.currentQuestion;
      if (!currentQ) {
        return false;
      }

      const currentQId = currentQ.questionId;
      const currentAnswerValue = this.answers[currentQId];

      // Check if the primary answer exists AND is valid
      if (currentAnswerValue === undefined || currentAnswerValue === null) {
        return false;
      }
      if (typeof currentAnswerValue === 'string' && currentAnswerValue.trim() === '') {
        return false;
      }
      if (currentAnswerValue === '') {
        return false;
      }

      // Check for validation errors (set by validateField, but don't call it here to avoid loops)
      if (this.fieldErrors && this.fieldErrors[currentQId]) {
        return false;
      }

      // Check for required associated input
      const mainAnswerConfig = Array.isArray(currentQ.answers)
        ? currentQ.answers.find((a) => a.value === currentAnswerValue)
        : null;

      if (
        mainAnswerConfig &&
        (mainAnswerConfig.type === 'text' || mainAnswerConfig.type === 'number')
      ) {
        const textInputKey = currentQId + '_input';
        const textInput = this.answers[textInputKey];
        if (textInput === undefined || textInput === null || textInput === '') {
          return false;
        }
      }

      // Check for unanswered or invalid follow-ups
      if (this.hasUnansweredFollowUps()) {
        return false;
      }

      // Check if current question is a single text input
      if (
        currentQ.answers &&
        currentQ.answers.length === 1 &&
        currentQ.answers[0].type === 'text' &&
        (!this.answers[currentQId] || 
         (typeof this.answers[currentQId] === 'string' && this.answers[currentQId].trim() === ''))
      ) {
        return false;
      }
      
      // Check if current question is a single number input
      if (
        currentQ.answers &&
        currentQ.answers.length === 1 &&
        currentQ.answers[0].type === 'number' &&
        (this.answers[currentQId] === undefined || 
         this.answers[currentQId] === null || 
         this.answers[currentQId] === '')
      ) {
        return false;
      }
      
      return true;
    },
  },
  mounted() {
    const store = useGlobalStore();
    if (store.state === 'CLOSED' || store.state === 'NOT_AVAILABLE') {
      this.$router.push({ path: '/', query: this.$route.query });
    }
    const theme = document.documentElement.getAttribute('data-theme');
    this.version = this.$route.query.version || clients[theme]?.version || 'v1';
    this.loadData();
  },
  methods: {
    async loadData() {
      const { t, locale } = useI18n();
      try {
        const response = await fetchQuestions(
          locale.value,
          this.$route.query.clientId,
          this.version,
          this.$route.query.type,
          this.$route.query.clinicId,
          this.$route.query.groupId
        );
        this.areas =
          this.version === 'v1' ? response.data : response.data.areas;
        this.version === 'v2' &&
          this.loadPredefinedAnswerSets(response.data.answers);
        // Handle incompleted questionnaire if it exists
        if (this.version === 'v2' && response.data.incompleted_questionnaire) {
          this.questionnaireId = response.data.incompleted_questionnaire.id;
          const preloaded = await this.preloadIncompletedAnswers(
            response.data.incompleted_questionnaire.responses
          );
          // Show the incomplete questionnaire modal with the date
          if (preloaded && response.data.incompleted_questionnaire.date) {
            // Format the date to show only the day
            const formattedDate = new Date(
              response.data.incompleted_questionnaire.date
            ).toLocaleDateString(this.$i18n.locale, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
            this.incompleteDate = formattedDate;
            this.showIncompleteModal = true;
          }
        } else {
          // Only set first question as current if we're not loading an incompleted questionnaire
          if (this.areas && this.areas.length > 0) {
            const firstQuestion = this.areas[0].questions[0];
            this.currentQuestionId = firstQuestion.questionId;
            this.updateNavigation(firstQuestion);
          }
        }

        this.areaProgress = this.areas.map((area) => {
          const completedQuestions = area.questions.filter(
            (q) => this.answers[q.questionId] !== undefined
          );
          return (completedQuestions.length / area.questions.length) * 100;
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response && error.response.status === 401) {
          this.errorMessage = t('not-authorized');
        } else {
          this.errorMessage = t('data-error');
        }
      }
    },
    loadPredefinedAnswerSets(answers) {
      this.predefinedAnswerSets = answers;
    },
    updateNavigation(currentQuestion) {
      const allQuestions = this.areas.flatMap((area) => area.questions);
      this.nextQuestionId = currentQuestion.nextQuestionId;
      this.previousQuestionId =
        allQuestions.find(
          (q) => q.nextQuestionId === currentQuestion.questionId
        )?.questionId || null;
    },

    // Handles blur/commit for standalone inputs (date, dropdown, number, text)
    handleStandaloneInputChange() {
      this.validateField(this.currentQuestion);
      this.updateAreaProgress();
    },

    async handleAnswerSelect(question, answer) {
      // Handle disclaimer for both V1 and V2
      if (answer.disclaimer && question.disclaimerText) {
        this.disclaimerModalText = question.disclaimerText;
        this.showDisclaimerModal = true;
      }

      if (this.version === 'v2') {
        // Get the actual answers array, whether it's direct or from predefined sets
        let actualAnswers = question.answers;
        if (
          typeof question.answers === 'string' &&
          this.predefinedAnswerSets[question.answers]
        ) {
          actualAnswers = this.predefinedAnswerSets[question.answers];
        }

        // First check if this is a multiple choice question without follow-ups
        const isMultipleChoice =
          Array.isArray(actualAnswers) &&
          actualAnswers.length > 1 &&
          actualAnswers.every(
            (a) =>
              !a.type ||
              !['dropdown', 'date', 'text', 'number'].includes(a.type)
          );

        const hasNoFollowUps =
          !question.followUpQuestions ||
          !question.followUpQuestions[answer.value] ||
          question.followUpQuestions[answer.value].length === 0;

        // Store the answer
        this.answers[question.questionId] = answer.value;

        // Clear any previous text input for this question if another radio is selected
        if (this.answers[question.questionId + '_input'] !== undefined) {
          delete this.answers[question.questionId + '_input'];
        }

        this.isProcessingAnswer = true;

        // Clear previous follow-ups when main answer changes
        this.currentFollowUpQuestions = [];

        // For multiple choice without follow-ups, or any choice without follow-ups or text input,
        // auto-advance immediately
        if (
          (isMultipleChoice && hasNoFollowUps) ||
          (hasNoFollowUps && answer.type !== 'text' && answer.type !== 'number')
        ) {
          this.updateAreaProgress();
          // Add small delay to prevent accidental double selections
          await new Promise((resolve) => setTimeout(resolve, 250));
          await this.goToNextQuestion();
          this.isProcessingAnswer = false;
          return;
        }

        // Check for follow-up questions
        if (
          question.followUpQuestions &&
          question.followUpQuestions[answer.value]
        ) {
          this.currentFollowUpQuestions = question.followUpQuestions[
            answer.value
          ].map((fq) => ({
            ...fq,
          }));
          this.updateAreaProgress();
          this.isProcessingAnswer = false;
          return;
        }

        // Check if the selected answer requires text/number input
        if (answer.type === 'text' || answer.type === 'number') {
          this.updateAreaProgress();
          await this.$nextTick();
          const inputEl = this.$el.querySelector(
            `input[name='question-${question.questionId}-input']`
          );
          if (inputEl) inputEl.focus();
          this.isProcessingAnswer = false;
          return;
        }

        this.updateAreaProgress();
        this.isProcessingAnswer = false;
      } else {
        // V1 logic
        this.answers[question.questionId] = answer.value;
        this.updateAreaProgress();
        // Add small delay to prevent accidental double selections
        await new Promise((resolve) => setTimeout(resolve, 250));
        this.goToNextQuestion();
      }
    },
    goToNextQuestion() {
      if (this.version === 'v2') {
        if (this.hasUnansweredFollowUps()) {
          return;
        }
        if (!this.canProceed) {
          return;
        }
      }

      // Find and set the next *main* question
      const mainNextQuestionId = this.nextQuestionId; // Get the ID stored for the main question

      if (mainNextQuestionId === null) {
        // Submit logic should be available now if this was the last question
        return;
      }

      const nextQuestion = this.areas
        .flatMap((area) => area.questions)
        .find((q) => q.questionId === mainNextQuestionId);

      if (nextQuestion) {
        this.currentQuestionId = nextQuestion.questionId;
        this.updateNavigation(nextQuestion); // Update next/previous based on the new question
        // Force a re-render of follow-ups
        this.$nextTick(() => {
          this.updateFollowUpQuestions(nextQuestion);
        });

        // Increment counter and save progress every 3 questions
        this.questionCounter++;
        if (this.questionCounter % 3 === 0) {
          this.saveQuestionnaireProgress();
        }
      }
    },

    goToPreviousQuestion() {
      const previousQuestion = this.areas
        .flatMap((area) => area.questions)
        .find((q) => q.nextQuestionId === this.currentQuestionId);
      if (previousQuestion) {
        this.currentQuestionId = previousQuestion.questionId;
        this.updateNavigation(previousQuestion);
        // Force a re-render of follow-ups
        this.$nextTick(() => {
          this.updateFollowUpQuestions(previousQuestion);
        });
      }
    },
    closeDisclaimerModal() {
      this.showDisclaimerModal = false;
    },

    handleSelectChange() {
      // v-model handles update
      this.updateAreaProgress();
    },

    storeInputOnBlur() {
      this.validateField(this.currentQuestion);
      this.currentFollowUpQuestions.forEach((fq) => {
        this.validateField(fq);
      });
      this.updateAreaProgress();
    },

    tryAdvanceInput(question) {
      // Only focus, do not auto-advance
      this.$nextTick(() => {
        if (!this.canProceed) {
          // Optional: Focus the input again or show validation message briefly
          const inputEl = this.$el.querySelector(
            `[name='question-${question.questionId}'], [name='question-${question.questionId}-input']`
          );
          if (inputEl) inputEl.focus();
        }
      });
    },

    handleFollowUpAnswerSelect(followUpQuestion, followUpAnswer) {
      // Handle selecting an answer for a follow-up question
      this.answers[followUpQuestion.questionId] = followUpAnswer.value;

      // Clear potential text input for other radio options in the *same* follow-up
      if (this.answers[followUpQuestion.questionId + '_input'] !== undefined) {
        delete this.answers[followUpQuestion.questionId + '_input'];
      }

      // Validate the follow-up question
      this.validateField(followUpQuestion);

      // Check for nested follow-ups *within* the current follow-up
      let nestedFollowUps = [];
      if (
        followUpQuestion.followUpQuestions &&
        followUpQuestion.followUpQuestions[followUpAnswer.value]
      ) {
        nestedFollowUps =
          followUpQuestion.followUpQuestions[followUpAnswer.value];
      }

      // Remove any deeper follow-ups that might have been displayed from a previous selection
      const currentFollowUpIndex = this.currentFollowUpQuestions.findIndex(
        (fq) => fq.questionId === followUpQuestion.questionId
      );
      this.currentFollowUpQuestions.splice(currentFollowUpIndex + 1);

      // Add nested follow-ups if any
      if (nestedFollowUps.length > 0) {
        this.currentFollowUpQuestions.splice(
          currentFollowUpIndex + 1,
          0,
          ...nestedFollowUps
        );
      }
      // Handle text input requirement for the *follow-up* answer
      if (followUpAnswer.type === 'text' || followUpAnswer.type === 'number') {
        this.updateAreaProgress(); // Update progress
        // Focus the input
        this.$nextTick(() => {
          const inputEl = this.$el.querySelector(
            `input[name='question-${followUpQuestion.questionId}-input']`
          );
          if (inputEl) inputEl.focus();
        });
        return;
      }

      this.updateAreaProgress();
    },

    handleFollowUpChange() {
      // Validate all follow-up questions
      this.currentFollowUpQuestions.forEach((fq) => {
        this.validateField(fq);
      });
      this.updateAreaProgress();
    },

    hasUnansweredFollowUps() {
      if (
        this.version !== 'v2' ||
        !this.currentFollowUpQuestions ||
        this.currentFollowUpQuestions.length === 0
      ) {
        return false;
      }

      const checkFollowUp = (fq) => {
        const questionId = fq.questionId;
        const mainAnswer = this.answers[questionId];
        const textInputKey = questionId + '_input';
        const textInputValue = this.answers[textInputKey];

        // First check validation
        try {
          if (!this.validateField(fq)) {
            return true; // Has validation error
          }
        } catch (error) {
          console.error('Error validating follow-up field:', error);
          return true; // Treat validation error as unanswered
        }

        // Handle single-answer questions (text, date, number)
        if (fq.answers && fq.answers.length === 1) {
          const answerType = fq.answers[0].type;
          if (
            answerType === 'text' ||
            answerType === 'date' ||
            answerType === 'number'
          ) {
            const isUnanswered =
              !mainAnswer || String(mainAnswer).trim() === '';
            return isUnanswered;
          }
        }

        // Check if main answer is missing or empty
        if (
          mainAnswer === undefined ||
          mainAnswer === null ||
          (typeof mainAnswer === 'string' && mainAnswer.trim() === '')
        ) {
          return true;
        }

        // Check for required associated input
        const answerConfig = Array.isArray(fq.answers)
          ? fq.answers.find((a) => a.value === mainAnswer)
          : null;

        if (
          answerConfig &&
          (answerConfig.type === 'text' || answerConfig.type === 'number') &&
          (!textInputValue || String(textInputValue).trim() === '')
        ) {
          return true;
        }

        // Check nested follow-ups if they exist
        if (fq.followUpQuestions && fq.followUpQuestions[mainAnswer]) {
          const nestedFollowUps = fq.followUpQuestions[mainAnswer];
          for (const nestedFq of nestedFollowUps) {
            if (checkFollowUp(nestedFq)) {
              return true;
            }
          }
        }

        return false;
      };

      return this.currentFollowUpQuestions.some((fq) => checkFollowUp(fq));
    },
    compileAnswers() {
      const responses = [];
      const allQuestions = this.areas.flatMap((area) => area.questions);
      allQuestions.forEach((question) => {
        const answerValue = this.answers[question.questionId];
        if (answerValue !== undefined) {
          let responseValue = answerValue;

          // Check if there's associated text input
          const textInputKey = question.questionId + '_input';
          if (this.answers[textInputKey] !== undefined) {
            const answerConfig = Array.isArray(question.answers)
              ? question.answers.find(
                  (a) =>
                    a.value === answerValue &&
                    (a.type === 'text' || a.type === 'number')
                )
              : null;
            if (answerConfig) {
              responseValue = this.answers[textInputKey];
            }
          }

          if (
            question.answers &&
            question.answers.length === 1 &&
            (question.answers[0].type === 'date' ||
              question.answers[0].type === 'text' ||
              question.answers[0].type === 'number')
          ) {
            responseValue = this.answers[question.questionId];
          }

          responses.push({
            questionId: question.questionId,
            responses: [responseValue],
          });
        }
      });

      // Add follow-up questions responses
      const followUpQuestionIds = new Set();
      allQuestions.forEach((q) => {
        if (q.followUpQuestions) {
          Object.values(q.followUpQuestions)
            .flat()
            .forEach((fq) => {
              followUpQuestionIds.add(fq.questionId);
              if (fq.followUpQuestions) {
                Object.values(fq.followUpQuestions)
                  .flat()
                  .forEach((nfq) => followUpQuestionIds.add(nfq.questionId));
              }
            });
        }
      });

      followUpQuestionIds.forEach((fqId) => {
        if (responses.some((r) => r.questionId === fqId)) return;

        const answerValue = this.answers[fqId];
        if (answerValue !== undefined) {
          let responseValue = answerValue;
          const textInputKey = fqId + '_input';

          if (this.answers[textInputKey] !== undefined) {
            let fqDefinition = null;
            allQuestions.some((q) => {
              if (q.followUpQuestions) {
                return Object.values(q.followUpQuestions)
                  .flat()
                  .some((fq) => {
                    if (fq.questionId === fqId) {
                      fqDefinition = fq;
                      return true;
                    }
                    if (fq.followUpQuestions) {
                      return Object.values(fq.followUpQuestions)
                        .flat()
                        .some((nfq) => {
                          if (nfq.questionId === fqId) {
                            fqDefinition = nfq;
                            return true;
                          }
                          return false;
                        });
                    }
                    return false;
                  });
              }
              return false;
            });

            const answerConfig =
              fqDefinition && Array.isArray(fqDefinition.answers)
                ? fqDefinition.answers.find(
                    (a) =>
                      a.value === answerValue &&
                      (a.type === 'text' || a.type === 'number')
                  )
                : null;
            const isTextOnlyFollowUp =
              fqDefinition &&
              fqDefinition.answers?.length === 1 &&
              (fqDefinition.answers[0].type === 'text' ||
                fqDefinition.answers[0].type === 'number');

            if (answerConfig || isTextOnlyFollowUp) {
              responseValue = this.answers[textInputKey] || this.answers[fqId];
            }
          }

          responses.push({
            questionId: fqId,
            responses: [responseValue],
          });
        }
      });

      return responses;
    },
    async submitAnswers() {
      try {
        // Track the time from app start until submit
        trackAppTiming('TimeToSubmit', {
          properties: {
            version: this.version,
            language: this.$i18n.locale,
            clientId: this.$route.query.clientId,
            professionalId: this.$route.query.professionalId,
          },
        });

        this.pageState = 'SENDING';
        await fetchScores(
          {
            iterations: [
              { iterationId: 'baseline', responses: this.compileAnswers() },
            ],
          },
          this.$i18n.locale,
          this.$route.query.clientId,
          this.$route.query.professionalId,
          this.version,
          this.questionnaireId,
          this.$route.query.type,
          this.$route.query.clinicId,
          this.$route.query.groupId
        );
        this.pageState = 'SENT';
      } catch (error) {
        console.error('Error submitting answers:', error);
        this.pageState = 'ERROR';
      }
    },
    updateAreaProgress() {
      this.areaProgress = this.areas.map((area) => {
        const completedQuestions = area.questions.filter((q) => {
          // V1 check
          if (this.version !== 'v2') {
            return this.answers[q.questionId] !== undefined;
          }

          // V2 check: Considers main answer, required inputs, and follow-ups
          const mainAnswer = this.answers[q.questionId];
          if (mainAnswer === undefined) return false; // Not answered at all

          // Check if answer requires text input
          const answerConfig = Array.isArray(q.answers)
            ? q.answers.find((a) => a.value === mainAnswer)
            : null;

          if (
            answerConfig &&
            (answerConfig.type === 'text' || answerConfig.type === 'number')
          ) {
            if (
              this.answers[q.questionId + '_input'] === undefined ||
              this.answers[q.questionId + '_input'] === ''
            ) {
              return false; // Required input missing
            }
          }

          // Check if this question triggered follow-ups that are NOT yet answered
          if (q.followUpQuestions && q.followUpQuestions[mainAnswer]) {
            const triggeredFollowUps = q.followUpQuestions[mainAnswer];
            const allTriggeredAnswered = triggeredFollowUps.every((fq) => {
              const fqAnswer = this.answers[fq.questionId];
              if (fqAnswer === undefined) return false;

              // Check text input for follow-up if required
              const fqAnswerConfig = Array.isArray(fq.answers)
                ? fq.answers.find((a) => a.value === fqAnswer)
                : null;
              if (
                fqAnswerConfig &&
                (fqAnswerConfig.type === 'text' ||
                  fqAnswerConfig.type === 'number')
              ) {
                if (
                  this.answers[fq.questionId + '_input'] === undefined ||
                  this.answers[fq.questionId + '_input'] === ''
                ) {
                  return false; // Follow-up input missing
                }
              }
              return true; // This triggered follow-up seems complete
            });
            if (!allTriggeredAnswered) {
              return false; // Not all follow-ups triggered by this question are complete
            }
          }

          // Check date/dropdown/text-only questions
          if (
            q.answers &&
            q.answers.length === 1 &&
            (q.answers[0].type === 'date' ||
              q.answers[0].type === 'dropdown' ||
              q.answers[0].type === 'text' ||
              q.answers[0].type === 'number')
          ) {
            if (
              this.answers[q.questionId] === undefined ||
              this.answers[q.questionId] === ''
            ) {
              return false;
            }
          }

          return true;
        });
        return (completedQuestions.length / area.questions.length) * 100;
      });
    },
    updateFollowUpQuestions(question) {
      this.currentFollowUpQuestions = [];

      if (this.version !== 'v2' || !question) {
        return;
      }

      const selectedAnswer = this.answers[question.questionId];
      if (selectedAnswer === undefined || selectedAnswer === null) {
        return;
      }

      let followUps = [];
      if (
        question.followUpQuestions &&
        question.followUpQuestions[selectedAnswer]
      ) {
        followUps = [...question.followUpQuestions[selectedAnswer]];
      }

      if (followUps.length === 0) {
        return;
      }

      this.currentFollowUpQuestions = JSON.parse(JSON.stringify(followUps));

      const restoreNestedFollowUps = (followUps) => {
        for (let i = 0; i < followUps.length; i++) {
          const fq = followUps[i];
          const fqAnswer = this.answers[fq.questionId];

          if (
            fqAnswer !== undefined &&
            fqAnswer !== null &&
            fq.followUpQuestions?.[fqAnswer]
          ) {
            const nestedFollowUps = [...fq.followUpQuestions[fqAnswer]];
            followUps.splice(i + 1, 0, ...nestedFollowUps);
            i += nestedFollowUps.length;
            restoreNestedFollowUps(nestedFollowUps);
          }
        }
      };

      restoreNestedFollowUps(this.currentFollowUpQuestions);
    },
    confirmExit() {
      this.showExitConfirmation = false;
      this.$router.push({ path: '/', query: this.$route.query });
    },
    validateField(question) {
      try {
        if (!question || !question.questionId) {
          return true;
        }
        // Ensure fieldErrors exists
        if (!this.fieldErrors) {
          this.fieldErrors = {};
        }
        
        const value = this.answers[question.questionId];
        const answerConfig = question.answers;
        
        // Clear previous error
        if (question.questionId) {
          delete this.fieldErrors[question.questionId];
        }
        
        // Helper to safely set error message (only if different to prevent infinite loops)
        const setError = (message) => {
          try {
            const errorKey = question.questionId;
            const currentError = this.fieldErrors[errorKey];
            let newError;
            
            if (typeof this.$t === 'function') {
              newError = this.$t(message);
            } else {
              newError = message;
            }
            
            // Only update if the error message actually changed
            if (currentError !== newError) {
              this.fieldErrors[errorKey] = newError;
            }
          } catch (e) {
            const errorKey = question.questionId;
            if (this.fieldErrors[errorKey] !== message) {
              this.fieldErrors[errorKey] = message;
            }
          }
        };
        
        const setErrorWithParams = (message, params) => {
          try {
            const errorKey = question.questionId;
            const currentError = this.fieldErrors[errorKey];
            let newError;
            
            if (typeof this.$t === 'function') {
              newError = this.$t(message, params);
            } else {
              newError = message;
            }
            
            // Only update if the error message actually changed
            if (currentError !== newError) {
              this.fieldErrors[errorKey] = newError;
            }
          } catch (e) {
            const errorKey = question.questionId;
            if (this.fieldErrors[errorKey] !== message) {
              this.fieldErrors[errorKey] = message;
            }
          }
        };

        // Special handling for follow-up questions with single text/number input
        if (answerConfig?.length === 1 && answerConfig[0].type) {
          const singleConfig = answerConfig[0];

          // Skip validation if empty (will be handled by canProceed)
          if (!value && value !== 0) {
            return true;
          }

          if (singleConfig.type === 'text') {
            // Ensure value is a string before accessing .length
            if (typeof value === 'string' && value.length > (singleConfig.maxLength || 1000)) {
              setErrorWithParams('text-too-long', {
                max: singleConfig.maxLength || 1000,
              });
              return false;
            }
          } else if (singleConfig.type === 'number') {
            // Convert to number for validation
            const numValue = Number(value);
            
            // Stricter number validation
            if (isNaN(numValue) || value === '' || value === null || value === undefined) {
              setError('must-be-number');
              return false;
            }

            // Check if it's an integer by checking for decimal points
            if (String(value).includes('.') || !Number.isInteger(numValue)) {
              setError('must-be-integer');
              return false;
            }

            if (numValue < 0) {
              setErrorWithParams('number-too-small', { min: 0 });
              return false;
            }
            if (
              singleConfig.max !== undefined &&
              numValue > singleConfig.max
            ) {
              setErrorWithParams('number-too-large', { max: singleConfig.max });
              return false;
            }
          }
          return true;
        }

        // Handle array of answers (radio buttons)
        if (Array.isArray(answerConfig)) {
          // Find the selected answer
          const selectedAnswer = answerConfig.find((a) => a.value === value);
          if (!selectedAnswer) {
            return true; // No validation needed if no option selected
          }
          // If selected answer requires text input
          if (
            selectedAnswer.type === 'text' ||
            selectedAnswer.type === 'number'
          ) {
            const inputValue = this.answers[question.questionId + '_input'];

            // Skip validation if empty (will be handled by canProceed)
            if (!inputValue && inputValue !== 0) {
              return true;
            }

            if (selectedAnswer.type === 'text') {
              // Ensure inputValue is a string before accessing .length
              if (typeof inputValue === 'string' && inputValue.length > (selectedAnswer.maxLength || 1000)) {
                this.fieldErrors[question.questionId] = this.$t('text-too-long', {
                  max: selectedAnswer.maxLength || 1000,
                });
                return false;
              }
            } else if (selectedAnswer.type === 'number') {
              // Convert to number for validation
              const numInputValue = Number(inputValue);
              
              // Stricter number validation
              if (isNaN(numInputValue) || inputValue === '' || inputValue === null || inputValue === undefined) {
                this.fieldErrors[question.questionId] = this.$t('must-be-number');
                return false;
              }

              // Check if it's an integer by checking for decimal points
              if (
                String(inputValue).includes('.') ||
                !Number.isInteger(numInputValue)
              ) {
                this.fieldErrors[question.questionId] =
                  this.$t('must-be-integer');
                return false;
              }

              if (numInputValue < 0) {
                try {
                  this.fieldErrors[question.questionId] = this.$t(
                    'number-too-small',
                    { min: 0 }
                  );
                } catch (e) {
                  this.fieldErrors[question.questionId] = 'Number must be 0 or greater';
                }
                return false;
              }
              if (
                selectedAnswer.max !== undefined &&
                numInputValue > selectedAnswer.max
              ) {
                this.fieldErrors[question.questionId] = this.$t(
                  'number-too-large',
                  { max: selectedAnswer.max }
                );
                return false;
              }
            }
          }
        }
        
        return true;
      } catch (error) {
        // Return false to prevent proceeding if validation crashes
        // This ensures the form doesn't crash and the user can fix the input
        return false;
      }
    },
    async preloadIncompletedAnswers(incompletedQuestionnaire) {
      try {
        // Get all questions in order from the API
        const allQuestions = this.areas.flatMap((area) => area.questions);

        // Create a map of question IDs to their responses from the incomplete questionnaire
        const incompleteResponses = new Map(
          incompletedQuestionnaire.map((item) => [
            item.questionId,
            item.responses[0],
          ])
        );

        // Get all possible follow-up questions from the API questions
        const getAllFollowUpQuestions = (questions) => {
          const followUps = new Set();
          questions.forEach((q) => {
            if (q.followUpQuestions) {
              Object.values(q.followUpQuestions)
                .flat()
                .forEach((fq) => {
                  followUps.add(fq.questionId);
                  // Handle nested follow-ups
                  if (fq.followUpQuestions) {
                    Object.values(fq.followUpQuestions)
                      .flat()
                      .forEach((nfq) => {
                        followUps.add(nfq.questionId);
                      });
                  }
                });
            }
          });
          return followUps;
        };

        const allFollowUpQuestionIds = getAllFollowUpQuestions(allQuestions);

        // Validate that all questions in the incomplete questionnaire exist in either main questions or follow-ups
        const hasInvalidQuestions = incompletedQuestionnaire.some(
          (item) =>
            !allQuestions.some((q) => q.questionId === item.questionId) &&
            !allFollowUpQuestionIds.has(item.questionId)
        );

        if (hasInvalidQuestions) {
          console.warn(
            'Invalid questions found in incomplete questionnaire, starting fresh'
          );
          // Reset the questionnaire ID to start fresh
          this.questionnaireId = null;
          // Set first question as current
          if (allQuestions.length > 0) {
            this.currentQuestionId = allQuestions[0].questionId;
            this.updateNavigation(allQuestions[0]);
          }
          return false;
        }

        // Process answers in the order of questions from the API
        let lastAnsweredQuestion = null;
        for (const question of allQuestions) {
          const response = incompleteResponses.get(question.questionId);
          if (response !== undefined) {
            this.answers[question.questionId] = response;
            lastAnsweredQuestion = question;

            // Check for follow-up questions for this answer
            if (
              question.followUpQuestions &&
              question.followUpQuestions[response]
            ) {
              const followUps = question.followUpQuestions[response];
              for (const followUp of followUps) {
                const followUpResponse = incompleteResponses.get(
                  followUp.questionId
                );
                if (followUpResponse !== undefined) {
                  this.answers[followUp.questionId] = followUpResponse;

                  // Handle nested follow-ups
                  if (
                    followUp.followUpQuestions &&
                    followUp.followUpQuestions[followUpResponse]
                  ) {
                    const nestedFollowUps =
                      followUp.followUpQuestions[followUpResponse];
                    for (const nestedFollowUp of nestedFollowUps) {
                      const nestedResponse = incompleteResponses.get(
                        nestedFollowUp.questionId
                      );
                      if (nestedResponse !== undefined) {
                        this.answers[nestedFollowUp.questionId] =
                          nestedResponse;
                      }
                    }
                  }
                } else {
                  // Found first unanswered follow-up
                  this.currentQuestionId = followUp.questionId;
                  this.updateNavigation(question); // Keep the main question as reference
                  await this.$nextTick();
                  this.updateFollowUpQuestions(question);
                  this.updateAreaProgress();
                  return true;
                }
              }
            }
          } else {
            // Found first unanswered question
            this.currentQuestionId = question.questionId;
            this.updateNavigation(question);
            await this.$nextTick();
            this.updateFollowUpQuestions(question);
            this.updateAreaProgress();
            return true;
          }
        }

        // If we get here, all questions were answered
        if (lastAnsweredQuestion) {
          this.currentQuestionId = lastAnsweredQuestion.questionId;
          this.updateNavigation(lastAnsweredQuestion);
          await this.$nextTick();
          this.updateFollowUpQuestions(lastAnsweredQuestion);
          this.updateAreaProgress();
          return true;
        }
      } catch (error) {
        console.error('Error preloading incomplete answers:', error);
        // Reset the questionnaire ID to start fresh
        this.questionnaireId = null;
        // Set first question as current
        const allQuestions = this.areas.flatMap((area) => area.questions);
        if (allQuestions.length > 0) {
          this.currentQuestionId = allQuestions[0].questionId;
          this.updateNavigation(allQuestions[0]);
        }
        return false;
      }
    },
    async saveQuestionnaireProgress() {
      const shouldSave =
        clients[document.documentElement.getAttribute('data-theme')]
          .saveProgress;
      if (!shouldSave) {
        return;
      }
      try {
        const response = await saveQuestionnaire(
          { responses: this.compileAnswers(), id: this.questionnaireId },
          this.$route.query.clientId,
          this.version,
          this.$route.query.type,
          this.$route.query.clinicId,
          this.$route.query.groupId
        );
        if (response.status === 200) {
          this.questionnaireId = response.data.questionnaireId;
        }
      } catch (error) {
        console.error('Error saving questionnaire progress:', error);
      }
    },
  },
};
</script>

<style scoped>
.circle-bg {
  stroke: #e6e6e6;
}
.circle-progress {
  stroke: black;
  transition: stroke-dashoffset 0.3s;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.sending-background {
  background-image: radial-gradient(circle at 10% 20%, rgba(129, 140, 248, 0.15) 0, transparent 40%),
    radial-gradient(circle at 80% 0, rgba(236, 72, 153, 0.1) 0, transparent 45%),
    radial-gradient(circle at 50% 100%, rgba(168, 85, 247, 0.12) 0, transparent 40%);
}

.sending-fact-card {
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08);
}

.loading-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.75rem;
}

.loading-dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background-color: #4b5563;
  animation: loading-bounce 1s infinite ease-in-out;
}

.loading-dot:nth-child(2) {
  animation-delay: 0.15s;
}

.loading-dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes loading-bounce {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  40% {
    transform: translateY(-4px);
    opacity: 1;
  }
}
</style>
